import { Injectable, inject } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  GET_DECLARANT_RATES,
  GET_DECLARANT_RATE,
} from '../../@graphql/operations/declarant-rate.queries';
import {
  CREATE_DECLARANT_RATE,
  UPDATE_DECLARANT_RATE,
} from '../../@graphql/operations/declarant-rate.mutations';
import {
  IDeclarantRate,
  ICreateDeclarantRateInput,
  IUpdateDeclarantRateInput,
} from '../../@interfaces/declarant-rate.interface';
import { CookieAuthService } from '../storage-variables/cookie-auth.service';

// The backend exposes DeclarantRate.id/id_declarant/id_type as the GraphQL
// `ID` scalar, which always serializes as a string over the wire — even
// though IDeclarantRate types them as `number`. Normalize here so every
// consumer gets real numbers (e.g. Int! mutation inputs downstream).
function normalizeRate(rate: IDeclarantRate): IDeclarantRate {
  return {
    ...rate,
    id: Number(rate.id),
    id_declarant: Number(rate.id_declarant),
    id_type: Number(rate.id_type),
  };
}

@Injectable({
  providedIn: 'root',
})
export class DeclarantRateService {
  private readonly cookieAuthService = inject(CookieAuthService);

  constructor(private apollo: Apollo) {}

  getDeclarantRates(idDeclarant?: number): Observable<IDeclarantRate[]> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const company = companyObject?.Id_company?.toString();

    return this.apollo
      .query<{ getDeclarantRates: IDeclarantRate[] }>({
        query: GET_DECLARANT_RATES,
        variables: { company, id_declarant: idDeclarant },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => result.data.getDeclarantRates.map(normalizeRate)),
        catchError((error) => {
          console.error(
            '[DeclarantRateService] Error getting declarant rates:',
            error,
          );
          return of([]);
        }),
      );
  }

  getDeclarantRate(id: number): Observable<IDeclarantRate | null> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const company = companyObject?.Id_company?.toString();

    return this.apollo
      .query<{ getDeclarantRate: IDeclarantRate | null }>({
        query: GET_DECLARANT_RATE,
        variables: { company, id },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) =>
          result.data.getDeclarantRate
            ? normalizeRate(result.data.getDeclarantRate)
            : null,
        ),
        catchError((error) => {
          console.error(
            '[DeclarantRateService] Error getting declarant rate:',
            error,
          );
          return of(null);
        }),
      );
  }

  // Errors are intentionally left to propagate here (no catchError): the
  // backend returns specific, user-facing validation messages (duplicate
  // rate, invalid FK, negative value, ...) that the form needs to display,
  // so swallowing them into null would hide why the operation failed.
  createDeclarantRate(
    input: ICreateDeclarantRateInput,
  ): Observable<IDeclarantRate | null> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const company = companyObject?.Id_company?.toString();

    return this.apollo
      .mutate<{ createDeclarantRate: IDeclarantRate }>({
        mutation: CREATE_DECLARANT_RATE,
        variables: { company, input },
      })
      .pipe(
        map((result) =>
          result.data?.createDeclarantRate
            ? normalizeRate(result.data.createDeclarantRate)
            : null,
        ),
      );
  }

  updateDeclarantRate(
    input: IUpdateDeclarantRateInput,
  ): Observable<IDeclarantRate | null> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const company = companyObject?.Id_company?.toString();

    return this.apollo
      .mutate<{ updateDeclarantRate: IDeclarantRate }>({
        mutation: UPDATE_DECLARANT_RATE,
        variables: { company, input },
      })
      .pipe(
        map((result) =>
          result.data?.updateDeclarantRate
            ? normalizeRate(result.data.updateDeclarantRate)
            : null,
        ),
      );
  }
}
